import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@material-ui/core';
import {
  useHistory, useLocation, useParams, useRouteMatch, Switch, Route, Redirect,
} from 'react-router-dom';

import { usePostHog } from 'posthog-js/react';

import { AccountIntent, ActivationProvider, useActivation } from './ActivationContext';
import { AuthMethodSelection } from './steps/AuthMethodSelection';
import { OtpVerification } from './steps/OtpVerification';
import { CoverageForm } from './steps/CoverageForm';
import { ActivationComplete } from './steps/ActivationComplete';
import { EmailCallback } from './steps/EmailCallback';
import { EmailEntry } from './steps/EmailEntry';
import { EmailAuthChoice } from './steps/EmailAuthChoice';
import { PasswordEntry } from './steps/PasswordEntry';
import { EmailSent } from './steps/EmailSent';
import { PasswordPrompt } from './steps/PasswordPrompt';
import { ChoosePassword } from './steps/ChoosePassword';
import { PasswordSaved } from './steps/PasswordSaved';

import { getSupabaseClient } from '@/utils/supabaseClient';
import { buildRedirectParam, isSafeRedirectUri } from '@/utils/urlHelpers';
import { activateSleepioConfirm } from '@/utils/sleepioActivationApi';
import { REDIRECT_TO_SESSION_KEY } from '@/utils/constants';
import { usePostHogDistinctId } from '@/hooks/usePostHogDistinctId';
import { SLEEPIO_CANOPY_PROGRAM } from '@/utils/posthog';

interface RouteParams {
  lob: string;
  // eslint-disable-next-line camelcase
  org_slug: string;
}

const ActivationRouterInner: React.FC = () => {
  const { path, url } = useRouteMatch();
  const location = useLocation();
  const history = useHistory();
  const { state, dispatch } = useActivation();
  const [sessionChecked, setSessionChecked] = useState(false);
  const posthog = usePostHog();
  const { distinctId: partnerDistinctId } = usePostHogDistinctId();

  // Stitch a partner-originated anonymous PostHog session into this CAP session.
  // When a landing page passes ph_token or ph_distinct_id in the URL, adopt that
  // identity so all activation events land on the same person as the pre-CAP funnel.
  useEffect(() => {
    if (partnerDistinctId) {
      posthog?.identify(partnerDistinctId);
    }
  }, [partnerDistinctId, posthog]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const redirectTo = params.get('redirectTo');
    if (redirectTo && isSafeRedirectUri(redirectTo)) {
      dispatch({ type: 'SET_REDIRECT_TO', redirectTo });
      sessionStorage.setItem(REDIRECT_TO_SESSION_KEY, redirectTo);
    }
  }, [dispatch, location.search]);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session } } = await getSupabaseClient().auth.getSession();
        // Some screens are legitimately reached *after* signing in, and the resume
        // below would throw a member straight past them. A member sitting on the
        // choose-a-password screen has a session by construction; without this guard a
        // page refresh mid-typing silently skips to the hand-off, and on the sign-up
        // route it is worse — the resume re-posts the activation confirmation and
        // signs the member out if that second call fails.
        const isPostAuthScreen = /\/(password|email)\//.test(location.pathname);
        if (session && !isPostAuthScreen) {
          dispatch({ type: 'SET_SESSION', session });
          const redirectParam = buildRedirectParam(
            new URLSearchParams(location.search).get('redirectTo'),
          );
          if (state.accountIntent === AccountIntent.Signin) {
            // A fresh magic-link login also lands here with a session already set —
            // don't count that as a "resumed" session.
            if (!location.pathname.includes('/email/callback')) {
              posthog?.capture('LoginSessionResumed', { program: SLEEPIO_CANOPY_PROGRAM });
            }
            dispatch({ type: 'SET_STEP', step: 'activation_complete' });
            history.replace(`${url}/complete${redirectParam}`);
          } else {
            try {
              await activateSleepioConfirm({ accessToken: session.access_token });
              dispatch({ type: 'SET_STEP', step: 'coverage_form' });
              history.replace(`${url}/coverage${redirectParam}`);
            } catch {
              await getSupabaseClient().auth.signOut();
              history.replace(`${url}/auth${redirectParam}`);
            }
          }
        }
      } finally {
        setSessionChecked(true);
      }
    };
    checkSession();
  }, []); // eslint-disable-line

  if (!sessionChecked) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  const authFallbackUrl = `${url}/auth${buildRedirectParam(
    new URLSearchParams(location.search).get('redirectTo'),
  )}`;

  return (
    <Switch>
      <Route exact path={`${path}/auth`} component={AuthMethodSelection} />
      <Route exact path={`${path}/otp`} component={OtpVerification} />
      <Route exact path={`${path}/email`} component={EmailEntry} />
      <Route exact path={`${path}/email/choose`} component={EmailAuthChoice} />
      <Route exact path={`${path}/email/password`} component={PasswordEntry} />
      <Route exact path={`${path}/email/callback`} component={EmailCallback} />
      <Route exact path={`${path}/sent`} component={EmailSent} />
      <Route exact path={`${path}/password/prompt`} component={PasswordPrompt} />
      <Route exact path={`${path}/password/new`} component={ChoosePassword} />
      <Route exact path={`${path}/password/saved`} component={PasswordSaved} />
      <Route exact path={`${path}/coverage`} component={CoverageForm} />
      <Route exact path={`${path}/complete`} component={ActivationComplete} />
      <Redirect to={authFallbackUrl} />
    </Switch>
  );
};

/** The intent this route represents. Derived, never stored — the path is the source. */
const intentForPath = (pathname: string): AccountIntent => (
  pathname.includes('/sign-up') ? AccountIntent.Create : AccountIntent.Signin
);

/**
 * Keeps `state.accountIntent` in step with the route it is derived from.
 *
 * `ActivationProvider` copies `initialAccountIntent` once, when `useReducer` sets up its
 * initial state, and nothing dispatched `SET_ACCOUNT_INTENT` afterwards. The provider does
 * not remount when the flow moves between a `/login` route and a `/sign-up` one either:
 * both are sibling `<Route>`s inside a single `<Switch>`, and React Router clones one
 * unkeyed child, so React reconciles the same instance.
 *
 * So a member sent from sign-in into activation kept `accountIntent: Signin` all the way to
 * the final step, where `ActivationComplete` reads it to decide whether to enrol them in the
 * clinical programme — and skipped the enrolment. They reached the app with a valid session
 * and no programme.
 *
 * Syncing here rather than at the one call site that pushes across the boundary, because the
 * defect is that the derived value went stale, not that one caller forgot to announce itself.
 */
const AccountIntentSync: React.FC = () => {
  const { pathname } = useLocation();
  const { state, dispatch } = useActivation();
  const accountIntent = intentForPath(pathname);

  useEffect(() => {
    if (state.accountIntent !== accountIntent) {
      dispatch({ type: 'SET_ACCOUNT_INTENT', accountIntent });
    }
  }, [accountIntent, state.accountIntent, dispatch]);

  return null;
};

const ActivationRouter: React.FC = () => {
  // eslint-disable-next-line camelcase
  const { org_slug: orgSlug } = useParams<RouteParams>();
  const { pathname } = useLocation();

  return (
    <ActivationProvider initialOrgSlug={orgSlug} initialAccountIntent={intentForPath(pathname)}>
      <AccountIntentSync />
      <ActivationRouterInner />
    </ActivationProvider>
  );
};

export default ActivationRouter;
